import { compact } from "lodash-es";
import { type NavigationGuard, type RouteLocationRaw, useRouter } from "vue-router";

import { SearchRouteHelper } from "@/helpers";
import { useDictionariesStore, useUserStore } from "@/stores";

const METHOD_IDS_WITH_STATIC_METADATA = [
  "cent-accounting-dada",
  "cent-accounting-daen",
  "cent-accounting-enda",
  "cent-accounting-enen",
  "cent-idioms-dada",
  "cent-music-dada"
];

interface LegacyParams {
  auto?: "auto";
  methodId?: string;
  query?: string;
  metadata0?: string;
  metadata1?: string;
  metadata2?: string;
  hash?: string;
}

interface Params {
  dictionaryId?: string;
  direction?: string;
  query?: string;
  staticMetadata?: Record<string, string>;
  temporaryMetadata?: Record<string, string[]>;
}

function parseLegacyHash(legacyHash: string): LegacyParams | undefined {
  let path = legacyHash;
  let hash = "";

  // Remove any article-section references, as the new site doesn't support it
  if (/#[^/].+/.test(path)) {
    path = path.slice(0, path.lastIndexOf("#"));
  }

  // Split on "?" if it belongs to a query param section
  if (/\?(?!\?).*?=/.test(legacyHash)) {
    const position = legacyHash.lastIndexOf("?");

    path = legacyHash.slice(0, position);
    hash = legacyHash.slice(position);
  }

  const patterns = [
    // #/auto/:method/:word/:metadata0?/:metadata1?/:metadata2?
    // #/:method/:word/:metadata0?/:metadata1?/:metadata2?
    new RegExp(
      "^#?" +
        "(?:/(?<auto>auto))?" +
        "(?:/(?<methodId>(?:[^/](?!auto))+))" +
        "(?:/(?<query>[^/]+))" +
        "(?:/(?<metadata0>[^/]+))?" +
        "(?:/(?<metadata1>[^/]+))?" +
        "(?:/(?<metadata2>[^/]+))?"
    ),

    // #/:word
    new RegExp("^#?(?:/(?<query>[^/]+))")
  ];

  for (const pattern of patterns) {
    const match = pattern.exec(path);

    if (match) {
      return {
        ...match.groups,
        hash
      };
    }
  }
}

/**
 * Convert legacy params
 */
function convertParams(legacyParams: LegacyParams): Params {
  const params: Params = {
    query: legacyParams.query,
    dictionaryId: undefined,
    direction: undefined,
    staticMetadata: undefined,
    temporaryMetadata: undefined
  };

  if (legacyParams.query) {
    params.query = decodeQuery(legacyParams.query);
  }

  if (legacyParams.methodId) {
    const dictionariesStore = useDictionariesStore();
    params.dictionaryId = dictionariesStore.getDictionaryByMethodId(legacyParams.methodId)?.id;
  }

  if (!legacyParams.auto) {
    params.direction = legacyParams.methodId;
  }

  if (!legacyParams.methodId || !legacyParams.metadata0) {
    return params;
  }

  if (METHOD_IDS_WITH_STATIC_METADATA.includes(legacyParams.methodId)) {
    params.staticMetadata = { [legacyParams.methodId]: legacyParams.metadata0 };
    legacyParams.metadata0 = undefined;
  }

  const temporaryMetadata = compact([legacyParams.metadata0, legacyParams.metadata1, legacyParams.metadata2]);
  if (temporaryMetadata) {
    params.temporaryMetadata = { [legacyParams.methodId]: temporaryMetadata };
  }

  return params;
}

/**
 * Decode query if it contains the identifier as the last part - but not
 * if it is the only part. Exceptions thrown when decoding will
 * be catched and the original query will be returned.
 */
function decodeQuery(query: string, identifier = "b64") {
  const position = query.indexOf(identifier);

  if (position < 1 || position !== query.length - identifier.length) {
    return query;
  }

  const encoded = query.slice(0, identifier.length * -1);
  const base64 = encoded.replace(/-/g, "=");

  try {
    query = atob(base64);
  } catch (exception) {
    return query;
  }

  return decodeURIComponent(query);
}

export function redirectLegacySearchGuard(): NavigationGuard {
  return (to, from, next) => {
    if (to.hash && to.meta.searchViewType) {
      const legacyParams = parseLegacyHash(to.hash);

      if (!legacyParams) {
        return next();
      }

      const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);
      const convertedLegacyParams = convertParams(legacyParams);

      const route = {
        name: searchRouteHelper.getSearchRouteName(),
        query: {
          allowSwitchDictionary: convertedLegacyParams.dictionaryId ? 0 : undefined,
          ...convertedLegacyParams
        },
        hash: legacyParams.hash ? `#${legacyParams.hash}` : ""
      };

      return next(route as unknown as RouteLocationRaw);
    }

    next();
  };
}
